<script lang="ts" setup>
import { computed, ref, nextTick, onMounted, onBeforeUnmount } from 'vue'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { formatIncidentType, formatDateTime, formatNumber } from '../../utils/format'
import { storeToRefs } from 'pinia'
import type { BountyDto } from '@roadfootage/api-client'
import { useSessionStore } from '../../stores/session'
import { useUserStore } from '../../stores/user/user.store'
import RespondToBountyDialog from './RespondToBountyDialog.vue'

const props = defineProps<{
  bounty: BountyDto
}>()

const { isLoggedIn } = storeToRefs(useSessionStore())
const { isRestricted } = storeToRefs(useUserStore())
const showRespond = ref(false)

const windowLabel = computed(() => {
  const start = formatDateTime(props.bounty.windowStartDateTime)
  const end = formatDateTime(props.bounty.windowEndDateTime)
  return `${start} — ${end}`
})

const mapContainer = ref<HTMLElement | null>(null)
let mapInstance: L.Map | null = null

function initMap() {
  if (!mapContainer.value || !props.bounty.location?.coordinates) return

  const [lng, lat] = props.bounty.location.coordinates

  const satelliteLayer = new L.TileLayer(
    'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    {
      attribution:
        'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
      maxZoom: 20
    }
  )

  mapInstance = new L.Map(mapContainer.value, {
    center: [lat, lng],
    zoom: 16,
    layers: [satelliteLayer],
    dragging: false,
    scrollWheelZoom: false,
    doubleClickZoom: false,
    boxZoom: false,
    keyboard: false,
    touchZoom: false,
    zoomControl: false
  })

  new L.Marker([lat, lng], {
    icon: new L.DivIcon({
      html: `<i class="q-icon mdi mdi-map-marker" style="font-size:32px;color:#e74c3c;filter:drop-shadow(0 1px 3px rgba(0,0,0,0.45))"></i>`,
      iconSize: [32, 32],
      iconAnchor: [16, 32],
      className: ''
    })
  }).addTo(mapInstance)
}

const isExpanded = ref(false)
const descriptionEl = ref<HTMLElement | null>(null)
const canExpand = ref(false)

onMounted(async () => {
  initMap()
  await nextTick()
  if (descriptionEl.value) {
    canExpand.value = descriptionEl.value.scrollHeight > descriptionEl.value.offsetHeight
  }
})

onBeforeUnmount(() => {
  mapInstance?.remove()
  mapInstance = null
})
</script>

<template>
  <q-card flat bordered class="bg-white">
    <template v-if="bounty.location?.coordinates">
      <div ref="mapContainer" class="bounty-map-thumbnail" />
    </template>

    <q-card-section class="q-pb-xs">
      <div class="row items-start no-wrap">
        <div class="col">
          <div class="text-body2 text-weight-bold text-blue-grey-9 ellipsis">
            {{ bounty.address?.formattedAddress || 'Location unknown' }}
          </div>
          <div class="text-caption text-blue-grey-6 q-mt-xs">
            <q-icon name="mdi-clock-outline" size="xs" class="q-mr-xs" />
            {{ windowLabel }}
          </div>
        </div>
        <q-chip
          icon="mdi-star-circle"
          color="blue-8"
          text-color="white"
          dense
          class="q-ml-sm q-mt-none"
        >
          {{ formatNumber(bounty.rewardAmountPoints ?? 0) }} pts
        </q-chip>
      </div>
    </q-card-section>

    <q-card-section class="q-pt-xs row q-gutter-xs">
      <q-chip :icon="'mdi-tag'" color="blue-grey-2" text-color="blue-grey-8" dense size="sm">
        {{ formatIncidentType(bounty.incidentReportType) }}
      </q-chip>
    </q-card-section>

    <q-separator />

    <q-card-section class="q-pa-sm">
      <div
        ref="descriptionEl"
        class="text-caption text-blue-grey-7"
        :class="{ 'ellipsis-3-lines': !isExpanded }"
      >
        {{ bounty.requestDescription }}
      </div>
      <div class="row justify-end q-mt-xs">
        <q-btn
          flat
          dense
          size="sm"
          color="blue-grey-6"
          :class="{ invisible: !canExpand }"
          :label="isExpanded ? 'Show less' : 'Show more'"
          :icon-right="isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
          @click="isExpanded = !isExpanded"
        />
      </div>
    </q-card-section>

    <q-card-actions v-if="isLoggedIn && !isRestricted" class="q-px-sm q-pb-sm">
      <q-btn
        unelevated
        no-caps
        color="blue-8"
        icon="mdi-video-plus"
        label="Respond with footage"
        class="full-width"
        data-testid="respond-with-footage"
        @click="showRespond = true"
      />
    </q-card-actions>

    <respond-to-bounty-dialog v-if="isLoggedIn" v-model="showRespond" :bounty="bounty" />
  </q-card>
</template>

<style lang="scss" scoped>
.bounty-map-thumbnail {
  height: 250px;
  width: 100%;
  overflow: hidden;
}
</style>
